import dayjs from 'dayjs';
import { formatDate } from '../../../helpers/UtilityHelper';
import { TOKEN_COLOR } from './tokens';

export const DEFAULT_DATE_RANGE_DAYS = 90;

// reason_master category holding the reasons for editing an ABD appointment (managed in Reason Master).
export const ABD_EDIT_REASON_CATEGORY = 'ABD_APPOINTMENT_EDIT';

export const getDefaultDateRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - DEFAULT_DATE_RANGE_DAYS);
    return { fromDate: from, toDate: to };
};

export const fmt = (d) => (d ? formatDate(d, 'yyyy-MM-dd') : null);

export const ABD_BASE_FILTER = [
    { field: 'shipmentType', operator: 'eq', value: 'B2B' },
    { field: 'appointdeliveryRequired', operator: 'eq', value: true },
];

/**
 * Closure category enum strings — match server values exactly. Centralized so
 * pages, modals, and analytics hooks all reference the same constants.
 */
export const CLOSURE_CATEGORIES = Object.freeze({
    PROZO: 'PROZO_ATTRIBUTABLE',
    NON_PROZO: 'NON_PROZO_ATTRIBUTABLE',
    FORCE_MAJEURE: 'FORCE_MAJEURE',
    JOINT: 'JOINT_ATTRIBUTABLE',
});

// Display data per closure category, in display order. Mark as Missed, Miss Reason Settings, the history
// view, the Miss Category filter and the Missed & Repeat cards all read from here.
export const CLOSURE_CATEGORY_META = Object.freeze([
    {
        value: CLOSURE_CATEGORIES.PROZO, label: 'Prozo Attributable', short: 'Our side',
        choice: 'Prozo was responsible', choiceHint: 'Our vehicle, documentation, or warehouse caused the miss',
        helper: 'Prozo Attributable: Prozo operations caused the missed appointment.',
        sectionSubtitle: 'Situations where Prozo or its operations caused the missed appointment',
        codePrefix: 'PROZO', color: TOKEN_COLOR.danger, bg: TOKEN_COLOR.dangerBg, kpiColor: 'red',
    },
    {
        value: CLOSURE_CATEGORIES.NON_PROZO, label: 'Non-Prozo Attributable', short: 'Brand / consignee',
        choice: 'Someone else was responsible', choiceHint: 'Brand, consignee, or external issue caused the miss',
        helper: 'Non-Prozo Attributable: brand, consignee, or external factors caused it.',
        sectionSubtitle: 'Brand, consignee, or external factors caused the missed appointment',
        codePrefix: 'CONSIGNEE', color: TOKEN_COLOR.primary, bg: TOKEN_COLOR.primaryBg, kpiColor: 'blue',
    },
    {
        value: CLOSURE_CATEGORIES.FORCE_MAJEURE, label: 'Force Majeure', short: 'Bandh, strike, flood',
        choice: 'Nobody could deliver (force majeure)', choiceHint: 'Bandh, strike, curfew, flood or a similar event',
        helper: 'Force Majeure: an event outside anyone\'s control; counted against neither side.',
        sectionSubtitle: 'Events outside anyone\'s control: bandh, strike, curfew, flood',
        codePrefix: 'FM', color: TOKEN_COLOR.accent, bg: TOKEN_COLOR.accentBg, kpiColor: 'purple',
    },
    {
        value: CLOSURE_CATEGORIES.JOINT, label: 'Joint Attributable', short: 'Both contributed',
        choice: 'Both sides were responsible', choiceHint: 'Prozo and the brand / consignee both contributed',
        helper: 'Joint: Prozo and the brand / consignee both contributed to the miss.',
        sectionSubtitle: 'Prozo and the brand / consignee both contributed to the miss',
        codePrefix: 'JOINT', color: TOKEN_COLOR.teal, bg: TOKEN_COLOR.tealBg, kpiColor: 'teal',
    },
]);

export const getClosureCategoryMeta = (value) => CLOSURE_CATEGORY_META.find((m) => m.value === value);

/** Order-status values that close out an ABD shipment (delivered or fail-terminal).
 *  Used in pill filters to exclude already-resolved orders from "active" buckets. */
const TERMINAL_OR_FAIL_STATUSES = Object.freeze([
    'CANCELLED_ORDER',
    'DELIVERED',
    'RTO_DELIVERED',
    'LOST',
    'DISPOSED_OFF',
    'DAMAGED',
]);

/** Sum of all miss buckets — used by KPI cards on grid pages. */
export const computeTotalMissed = (kpi = {}) =>
    (kpi.miss1 || 0) + (kpi.miss2 || 0) + (kpi.miss3plus || 0);

export const ABD_INCLUDES = [
    'appointmentDeliveryDate',
    'appointmentDelivery',
    'appointmentDeliveryData',
    'appointmentDeliveryHistory',
    'appointmentDeliveryHistoryCount',
    'edd',
    'orderDate',
    'id',
    'reference',
    'invoiceValue',
    'pods',
    'shippingLabelId',
    'awbRegisteredDate',
    'merchantPricing.zone',
    'merchantPricing.price',
    'merchantDetail',
    'courierDetail',
    'pickupDetails.from_pincode',
    'pickupDetails.from_city',
    'deliveryDetails.to_pincode',
    'deliveryDetails.to_state',
    'deliveryDetails.to_city',
    'channelName',
    'orderStatus',
    'shipmentDetail',
    'paymentMode',
    'awbNumber',
    'referenceNumber',
    'additionalData.soldToPartyName',
    'soldToPartyDetails.name',
    'soldToPartyDetails.address',
    'soldToPartyDetails.city',
    'soldToPartyDetails.state',
    'soldToPartyDetails.pincode',
    'appointdeliveryRequired',
    'terminalStatus',
    // Read by the Schedule popup's docs-only save (it re-sends the saved extra docs).
    'additionalDocuments',
].join(',');

// The latest miss = last entry of appointmentDeliveryHistory (read from history; nothing is copied onto the order).
export const lastMiss = (row) => {
    const h = row?.appointmentDeliveryHistory;
    return Array.isArray(h) && h.length ? h[h.length - 1] : null;
};

export const PILL_KEYS = {
    ALL: 'all',
    ACTIVE: 'active',
    MISS_1: 'miss1',
    MISS_2: 'miss2',
    MISS_3_PLUS: 'miss3plus',
    NEEDS_SCHEDULING: 'needsScheduling',
    DELIVERED: 'delivered',
    PROZO_FAULT: 'prozoFault',
    NON_PROZO: 'nonProzo',
    EVENTUALLY_DELIVERED: 'eventuallyDelivered',
    PROZO_FAULT_PENDING: 'prozoFaultPending',
    // Still open, by the category of the LATEST miss (one bucket per shipment).
    FORCE_MAJEURE: 'forceMajeure',
    JOINT: 'joint',
    OPEN_MISSED: 'openMissed',
    // Missed at least once, still open, next appointment already booked and not yet passed.
    NEW_DATE_FIXED: 'newDateFixed',
    // ABD on, first appointment not booked, never missed. Outside the Missed & Repeat baseline (>= 1 miss).
    WAITING_FIRST_APPOINTMENT: 'waitingFirstAppointment',
    // Shipments with >=1 missed appointment regardless of current status.
    // This is the shipment-level twin of the analytics "missed appointments"
    // instance count, and equals the Missed & Repeat "All Missed" grid/export.
    MISSED_ANY: 'missedAny',
};

// Reusable terminal/fail exclusion: shipment is still in-flight (not delivered,
// cancelled, RTO-delivered, lost, or damaged). Applied to every pill whose
// semantics require "still-pending" shipments — sum/list parity holds because
// pill counts (BE bucket facet) and pill lists (FE search) share this builder.
const NOT_TERMINAL_FILTERS = [
    { field: 'terminalStatus', operator: 'exists', value: false },
    { field: 'orderStatus', operator: 'nin', value: TERMINAL_OR_FAIL_STATUSES },
];

const dateValue = (d) => `Date('${d.format('YYYY-MM-DD HH:mm:ss')}')`;

// Category of the latest miss (last appointmentDeliveryHistory entry), so each shipment counts in one card.
const latestCategoryFilter = (category) => [
    { field: 'appointmentDeliveryHistory.closureCategory', operator: 'lasteq', value: category },
    ...NOT_TERMINAL_FILTERS,
];

export const buildPillFilter = (key) => {
    switch (key) {
        case PILL_KEYS.ACTIVE:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: true },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_1:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 1 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_2:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 2 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_3_PLUS:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 3 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.NEEDS_SCHEDULING:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.DELIVERED:
            return [{ field: 'orderStatus', operator: 'eq', value: 'DELIVERED' }];
        case PILL_KEYS.PROZO_FAULT:
            return latestCategoryFilter(CLOSURE_CATEGORIES.PROZO);
        case PILL_KEYS.NON_PROZO:
            return latestCategoryFilter(CLOSURE_CATEGORIES.NON_PROZO);
        case PILL_KEYS.FORCE_MAJEURE:
            return latestCategoryFilter(CLOSURE_CATEGORIES.FORCE_MAJEURE);
        case PILL_KEYS.JOINT:
            return latestCategoryFilter(CLOSURE_CATEGORIES.JOINT);
        case PILL_KEYS.OPEN_MISSED:
            return NOT_TERMINAL_FILTERS;
        case PILL_KEYS.WAITING_FIRST_APPOINTMENT:
            return WAITING_FIRST_APPOINTMENT_FILTER;
        case PILL_KEYS.NEW_DATE_FIXED:
            return [
                { field: 'appointmentDelivery', operator: 'eq', value: true },
                { field: 'appointmentDeliveryData.appointmentDeliveryEndTime', operator: 'gte', value: dateValue(dayjs().startOf('day')) },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.EVENTUALLY_DELIVERED:
            return [
                { field: 'orderStatus', operator: 'eq', value: 'DELIVERED' },
                { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 },
            ];
        case PILL_KEYS.MISSED_ANY:
            return [{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }];
        case PILL_KEYS.PROZO_FAULT_PENDING:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
                ...NOT_TERMINAL_FILTERS,
                { field: 'appointmentDeliveryHistory.closureCategory', operator: 'eq', value: 'PROZO_ATTRIBUTABLE' },
            ];
        case PILL_KEYS.ALL:
        default:
            return [];
    }
};

// Overdue appointments: still open (not marked missed) on any day after the appointment date, i.e. the slot
// ended before `todayStart` (start of today, IST). Fixed business rule (ops decision), not a setting.
// Shipments already delivered / RTO / cancelled are left out: no next appointment is needed for them.
export const buildOverdueFilter = (todayStart) => [
    { field: 'appointmentDelivery', operator: 'eq', value: true },
    { field: 'appointmentDeliveryData.appointmentDeliveryEndTime', operator: 'lt', value: dateValue(todayStart) },
    ...NOT_TERMINAL_FILTERS,
];

// Missed, reason given, no new appointment booked yet (still open).
export const NO_NEW_DATE_FILTER = [
    { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 },
    { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
    ...NOT_TERMINAL_FILTERS,
];

// ABD on but the first appointment is not booked yet (no appointment, never missed, still open).
export const WAITING_FIRST_APPOINTMENT_FILTER = [
    { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
    { field: 'appointmentDeliveryHistory.0', operator: 'exists', value: false },
    ...NOT_TERMINAL_FILTERS,
];

// Days since the last missed appointment date (start of the latest history entry). maxDays null = no upper limit.
export const NO_NEW_DATE_AGE_BANDS = Object.freeze([
    { key: 'age1', label: '0 – 6 days', minDays: 0, maxDays: 6, hint: 'Recent', kpiColor: 'grey', color: '#78909c', bg: '#eceff1' },
    { key: 'age2', label: '7 – 14 days', minDays: 7, maxDays: 14, hint: 'Follow up', kpiColor: 'amber', color: TOKEN_COLOR.warning, bg: TOKEN_COLOR.softYellowBg },
    { key: 'age3', label: '15 – 22 days', minDays: 15, maxDays: 22, hint: 'Escalate', kpiColor: 'red', color: '#e53935', bg: TOKEN_COLOR.dangerBg },
    { key: 'age4', label: '23+ days', minDays: 23, maxDays: null, hint: 'Call today', kpiColor: 'red', color: '#8e0000', bg: TOKEN_COLOR.dangerBg },
]);

export const ageBandFilter = (band, todayStart) => {
    const arr = [{ field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', operator: 'lastlt', value: dateValue(todayStart.subtract(band.minDays - 1, 'day')) }];
    if (band.maxDays != null) {
        arr.push({ field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', operator: 'lastgte', value: dateValue(todayStart.subtract(band.maxDays, 'day')) });
    }
    return arr;
};

export const MISS_CATEGORY_OPTIONS = [
    { value: '', label: 'All' },
    ...CLOSURE_CATEGORY_META.map((m) => ({ value: m.value, label: m.label })),
];

export const DOCUMENTS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'UPLOADED', label: 'Uploaded' },
    { value: 'NOT_UPLOADED', label: 'Not uploaded' },
];

// Where a shipment's documents can live: uploaded before any appointment (additionalDocuments),
// on the active appointment, or on an earlier (closed) one. Any of these set = "uploaded".
// `extraDocs.0` = the extra-docs list is non-empty.
const DOC_PATHS = ['invoiceS3Key', 'ewayBillS3Key', 'poCopyS3Key', 'extraDocs.0'].map((f) => `additionalDocuments.${f}`);
const APPT_DOC_KEYS = ['appointmentDeliveryInvoiceS3Key', 'appointmentDeliveryEwayBillS3Key', 'appointmentDeliveryPOCopyS3Key', 'extraDocs.0'];
const DOCUMENT_FIELDS = [
    ...DOC_PATHS,
    ...APPT_DOC_KEYS.map((f) => `appointmentDeliveryData.${f}`),
    ...APPT_DOC_KEYS.map((f) => `appointmentDeliveryHistory.${f}`),
];

const documentsFilter = (documents) => {
    if (documents === 'UPLOADED') {
        // Server operator: one condition, at least one of the fields set (combines with all other filters).
        return [{ field: 'documents', operator: 'anynotnull', value: DOCUMENT_FIELDS }];
    }
    if (documents === 'NOT_UPLOADED') {
        return DOCUMENT_FIELDS.map((field) => ({ field, operator: 'eq', value: null }));
    }
    return [];
};

// Bucket counts driven entirely from FE — single source of truth.
// Each name is a $facet stage in the dashboard endpoint pipeline.
// Used by useAppointmentDashboard for pill counters + KPI cards.
export const BUCKET_KEYS = [
    PILL_KEYS.ALL,
    PILL_KEYS.ACTIVE,
    PILL_KEYS.MISS_1,
    PILL_KEYS.MISS_2,
    PILL_KEYS.MISS_3_PLUS,
    PILL_KEYS.NEEDS_SCHEDULING,
    PILL_KEYS.DELIVERED,
    PILL_KEYS.PROZO_FAULT,
    PILL_KEYS.NON_PROZO,
    PILL_KEYS.EVENTUALLY_DELIVERED,
    PILL_KEYS.PROZO_FAULT_PENDING,
    PILL_KEYS.MISSED_ANY,
    PILL_KEYS.FORCE_MAJEURE,
    PILL_KEYS.JOINT,
    PILL_KEYS.NEW_DATE_FIXED,
];

/**
 * Single source of truth for the {@code andfilter} portion of the shared
 * baseline scope. Used by BOTH /order/search (grid) and
 * /appointment_delivery/dashboard so counts/rows always match.
 */
export const buildBaselineAndFilter = (filters = {}) => {
    const arr = [...ABD_BASE_FILTER];
    if (filters.fromDate) {
        arr.push({ field: 'awbRegisteredDate', operator: 'gte', value: `Date('${fmt(filters.fromDate)} 00:00:00')` });
    }
    if (filters.toDate) {
        arr.push({ field: 'awbRegisteredDate', operator: 'lte', value: `Date('${fmt(filters.toDate)} 23:59:59')` });
    }
    if (filters.merchant) {
        arr.push({ field: 'merchant.$id', operator: 'eq', value: `ObjectId('${filters.merchant}')` });
    }
    if (filters.courier) {
        arr.push({ field: 'courier.$id', operator: 'eq', value: `ObjectId('${filters.courier}')` });
    }
    if (filters.city) {
        arr.push({ field: 'deliveryDetails.to_city', operator: 'eq', value: filters.city });
    }
    if (filters.state) {
        arr.push({ field: 'deliveryDetails.to_state', operator: 'eq', value: filters.state });
    }
    if (filters.awb) {
        arr.push({ field: 'awbNumber', operator: 'eq', value: filters.awb });
    }
    if (filters.missCategory) {
        arr.push({ field: 'appointmentDeliveryHistory.closureCategory', operator: 'eq', value: filters.missCategory });
        // Miss-attribution filter implies the user wants still-pending shipments
        // with that attribution — without this exclusion, delivered/cancelled/
        // RTO orders that ever had a Prozo (or Non-Prozo) miss in history also
        // surface, which is misleading on grid + KPI counts.
        arr.push({ field: 'terminalStatus', operator: 'exists', value: false });
        arr.push({ field: 'orderStatus', operator: 'nin', value: TERMINAL_OR_FAIL_STATUSES });
    }
    arr.push(...documentsFilter(filters.documents));
    if (filters.attempts) {
        const n = parseInt(filters.attempts, 10);
        if (n === 4) {
            arr.push({ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 4 });
        } else if (!Number.isNaN(n)) {
            arr.push({ field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: n });
        }
    }
    return arr;
};

/**
 * Single source of truth for the {@code orfilter} portion of the shared
 * baseline scope. Today carries only the {@code reference} substring match
 * (against reference + shippingLabelId). Both grid and dashboard hooks send
 * this so scope stays in lockstep when user types a reference.
 */
export const buildBaselineOrFilter = (filters = {}) => {
    const arr = [];
    if (filters.reference) {
        arr.push({ field: 'reference', operator: 'like', value: `${filters.reference}.*` });
        arr.push({ field: 'shippingLabelId', operator: 'like', value: `${filters.reference}.*` });
    }
    return arr;
};

export const ATTEMPT_OPTIONS = [
    { value: '', label: 'All' },
    { value: '1', label: '1 attempt' },
    { value: '2', label: '2 attempts' },
    { value: '3', label: '3 attempts' },
    { value: '4', label: '4+ attempts' },
];
